import React, {useEffect, useState} from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {ObjectId} from 'bson';
import {useForm} from 'react-hook-form';
import {
  useCreateIntentSource,
  useEditIntentSource,
} from '../../hooks/api/intent';
import {IntentSourceLabel, WebIntentSourcePath} from '../../shared/validation';

type WebIntentSourceFormData = {
  label: string;
  path: string;
};

export const WebIntentSourceModal = ({
  isOpen,
  onClose,
  intentSource,
}: {
  isOpen: boolean;
  onClose: () => void;
  intentSource?: {id: ObjectId; label: string; path: string};
}) => {
  const toast = useToast();
  const createSource = useCreateIntentSource();
  const editSource = useEditIntentSource();

  const isEdit = !!intentSource;

  const [pathChangeAcknowledged, setPathChangeAcknowledged] = useState(false);

  const {
    handleSubmit,
    register,
    reset: resetForm,
    watch,
    formState: {errors},
  } = useForm<WebIntentSourceFormData>({
    defaultValues: {label: '', path: ''},
  });

  const currentPath = watch('path');
  const hasPathChanged = isEdit && currentPath?.trim() !== intentSource?.path;

  // Reset form when modal opens based on source prop
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    resetForm(
      intentSource
        ? {label: intentSource.label, path: intentSource.path}
        : {label: '', path: ''}
    );
    setPathChangeAcknowledged(false);
  }, [isOpen, intentSource, resetForm]);

  const onSubmit = (data: WebIntentSourceFormData) => {
    const onSuccess = () => {
      toast({
        status: 'success',
        title: isEdit ? 'Source updated.' : 'Source created.',
        duration: 2_000,
        isClosable: true,
      });
      onClose();
    };

    if (isEdit) {
      editSource.mutate(
        {
          sourceId: intentSource.id,
          label: data.label.trim(),
          path: data.path.trim(),
          type: 'webIntent',
        },
        {onSuccess}
      );
    } else {
      createSource.mutate(
        {
          label: data.label.trim(),
          path: data.path.trim(),
          type: 'webIntent',
        },
        {onSuccess}
      );
    }
  };

  const isPending = createSource.isPending || editSource.isPending;

  return (
    <Modal isCentered={true} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader fontWeight="normal" pb="1" pt="6">
          {isEdit ? 'Edit Source' : 'Create Source'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px="6" pt="6" pb="8">
          <VStack spacing="4">
            <FormControl isInvalid={!!errors.label}>
              <FormLabel>Label</FormLabel>
              <Input
                placeholder="Enter label (e.g., 'Pricing Page')"
                {...register('label', {
                  required: true,
                  minLength: IntentSourceLabel.minLength,
                  maxLength: IntentSourceLabel.maxLength,
                  pattern: IntentSourceLabel.pattern,
                })}
              />
              <FormErrorMessage>
                Label must be between {IntentSourceLabel.minLength} and{' '}
                {IntentSourceLabel.maxLength} characters.
              </FormErrorMessage>
              {isEdit && (
                <FormHelperText color="kgray.300">
                  Changing the label will also update all associated field
                  definition labels.
                </FormHelperText>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.path}>
              <FormLabel>Path</FormLabel>
              <Input
                placeholder="/pricing"
                autoComplete="off"
                fontFamily="mono"
                {...register('path', {
                  required: true,
                  minLength: WebIntentSourcePath.minLength,
                  maxLength: WebIntentSourcePath.maxLength,
                })}
              />
              <FormErrorMessage>
                Path must be between {WebIntentSourcePath.minLength} and{' '}
                {WebIntentSourcePath.maxLength} characters.
              </FormErrorMessage>

              {isEdit && (
                <Flex direction="column" gap="2">
                  <FormHelperText
                    color={hasPathChanged ? 'korange.400' : 'kgray.300'}
                  >
                    Changing the path will delete all historical intent data.
                  </FormHelperText>
                  <Checkbox
                    isChecked={pathChangeAcknowledged}
                    visibility={hasPathChanged ? 'visible' : 'hidden'}
                    onChange={(e) =>
                      setPathChangeAcknowledged(e.target.checked)
                    }
                  >
                    I understand and want to proceed.
                  </Checkbox>
                </Flex>
              )}
            </FormControl>
          </VStack>
        </ModalBody>
        <Divider />
        <ModalFooter justifyContent="start" gap="4">
          <Button
            colorScheme="kbuttonblue"
            type="submit"
            isLoading={isPending}
            isDisabled={hasPathChanged && !pathChangeAcknowledged}
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
          <Button color="kgray.300" onClick={onClose} variant="ghost">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
